<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to Company Menu" @click="$router.push('/company')">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <help-link appId="attendance" :pageId="helpPageId" color="white"></help-link>
                    <v-menu left v-model="menu_status" offset-y> 
                        <template v-slot:activator="{ on }">
                            <v-btn color="primary" dark v-on="on">
                                &nbsp;&nbsp;CY {{selectedFiscalYear}}&nbsp;&nbsp;
                                <v-icon v-show="!menu_status">arrow_drop_down</v-icon>
                                <v-icon v-show="menu_status">arrow_drop_up</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-tile v-for="(item,index) in fiscal_years" :key="index" :title="`Calendar Year ${item.fiscalYear}`" @click="setSelectedYear(item.fiscalYear)">
                                <v-list-tile-title small>CY {{item.fiscalYear}}</v-list-tile-title>
                            </v-list-tile>
                        </v-list>
                    </v-menu>
                </v-toolbar>
            </v-card>
            <v-card class="elevation-1 mt-5"> 
                <v-toolbar flat height="45px">
                    <div :class="isMobile ? 'wp-60 mb-10':'wp-30 mb-10'">
                        <v-text-field v-model="search" append-icon="search" :label="searchLabel" single-line hide-details clearable></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                    <span>
                        <v-switch
                            v-model="viewAll"
                            label="View all"
                            color="primary"
                            hide-details
                        ></v-switch> 
                    </span>
                </v-toolbar>

                <!-- by cutoff -->
                <v-data-table
                    :headers="isMobile?header_mobile:headers"
                    :items="overtime_summary_filtered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync1"
                    v-show="!viewAll"
                >
                    <template v-slot:items="props">
                        <tr @click="viewCutoffDetails(props.item)" class="clickable" title="Click to view timesheet for this period">
                            <td v-if="isMobile">
                                <span :class="isCurrentPeriod(props.item)?'font-weight-bold':''">
                                    {{ getCutoffCoverageDescription(props.item.cutOffStart, props.item.cutOffEnd) }}
                                </span>
                            </td>
                            <td v-if="!isMobile">
                                <span :class="isCurrentPeriod(props.item)?'font-weight-bold':''">{{props.item.cutOffPeriodDesc}}</span>
                            </td>
                            <td v-if="!isMobile">{{ props.item.cutOffStart | moment("YYYY-MM-DD") }}</td>
                            <td v-if="!isMobile">{{ props.item.cutOffEnd | moment("YYYY-MM-DD") }}</td>
                            <td>
                                <b :class="commonfn.getStatusColor(props.item.cutOffStatus)">{{commonfn.getStatusDescription(props.item.cutOffStatus)}}</b>
                            </td>
                        </tr>
                    </template>
                </v-data-table> 

                <!-- by calendar year -->
                <v-data-table
                    :headers="isMobile?headers_all_mobile:headers_all"
                    :items="overtime_all_filtered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync2"
                    v-show="viewAll"
                >
                    <template v-slot:items="props">
                        <tr @click="viewDetails(props.item)" class="clickable" title="Click to view timesheet for this period">
                            <!-- replace with team chips component -->
                            <!-- <td>{{ props.item.teamName }}</td> -->
                            <td>
                                <v-chip class="user-chip-container">
                                    <v-avatar>
                                        <v-img :src="awsS3Helper.getAvatarUrl(props.item.userId)"></v-img>
                                    </v-avatar>
                                    {{props.item.firstName}} {{props.item.lastName}}
                                </v-chip>
                            </td>
                            <td>
                                <span v-if="isMobile">{{ props.item.transDate | moment('MMM D') }}</span>
                                <span v-else>{{ props.item.transDate | moment('YYYY-MM-DD (ddd)') }}</span>
                            </td>
                            <td v-if="!isMobile">{{props.item.startTime|moment('hh:mm a')}}-{{props.item.endTime|moment('hh:mm a')}}</td>
                            <td v-if="!isMobile">
                                <span v-if="props.item.offsetOrPay==1">Time Off in Lieu (TOIL</span>
                                <span v-else>Payroll (Paid Overtime)</span>
                            </td>
                            <td v-if="!isMobile">{{ props.item.hours }}</td>
                            <td v-if="!isMobile">{{ props.item.chargeableHours }}</td>
                            <td>
                                <b :class="commonfn.getStatusColor(props.item.status)">{{commonfn.getStatusDescription(props.item.status)}}</b>
                            </td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>
        </v-flex>
    </v-layout> 
</template>

<script>

import overtimeApi from '@/api/overtime'
import commonfn from '@/common/functions'
import awsS3Helper from '@/helpers/aws-s3'
import rowsPerPageItems from '@/constants/rows-per-page'
import { getSort, getRowsPerPage, getViewAllSwitch, getCalendarYear, getSearchKeyword, updateCalendarYear, updateRowsPerPage, updateSort, updateViewAllSwitch, updateSearchKeyword } from '@/helpers/user-prefs'
import { getCutoffCoverageDescription } from '@/helpers/cutoff-format'

export default {     
    name: 'Overtime',
    metaInfo: {
        title: 'Overtime',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Overtime' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
    },   
    data () {
        const routeName1 = `${this.$route.name}-table1`
        const routeName2 = `${this.$route.name}-table2`
        const savedSort1 = getSort(routeName1)
        const savedSort2 = getSort(routeName2)

        return {
            rowsPerPageItems,
            commonfn,
            awsS3Helper,
            getCutoffCoverageDescription,
            paginationSync1: {
                rowsPerPage: getRowsPerPage(routeName1),
                sortBy: savedSort1.sortBy,
                descending: savedSort1.descending,
            },
            paginationSync2: {
                rowsPerPage: getRowsPerPage(routeName2),
                sortBy: savedSort2.sortBy,
                descending: savedSort2.descending,
            },
            viewAll: getViewAllSwitch(this.$route.name),
            selectedFiscalYear: getCalendarYear(this.$route.name),
            search: getSearchKeyword(this.$route.name),
            error: false,
            errorMessage: '',
            loading: false,
            headers: [
                {text: 'Period',align: 'left',value: 'cutOffPeriodDesc', width: '10%', sortable:true},
                {text: 'Cutoff Start Date',align: 'left',value: 'cutOffStart', width: '10%', sortable:true},
                {text: 'Cutoff End Date',align: 'left',value: 'cutOffEnd', width: '10%', sortable:true},
                {text: 'Cutoff Status',align: 'left',value: 'cutOffStatus',  width: '10%', sortable:true},
            ],
            header_mobile: [
                {text: 'Cutoff Period',align: 'left',value: 'cutOffStart', width: '50%', sortable:true},
                {text: 'Cutoff Status',align: 'left',value: 'cutOffStatus',  width: '50%', sortable:true},
            ],
            headers_all: [
                // {text: 'Team',align: 'left',value: 'teamName', width:'10%', sortable:true},
                {text: 'Employee',align: 'left',value: 'lastName', width:'15%', sortable:true},
                {text: 'Date',align: 'left',value: 'transDate', width:'10%', sortable:true},
                {text: 'Start/End',align: 'left',value: 'startTime', width:'10%',sortable:true}, 
                {text: 'Charging Type',align: 'left',value: 'offsetOrPay', width:'10%',sortable:true},
                {text: 'Total Hours',align: 'left',value: 'hours', width:'5%',sortable:true},
                {text: 'Chargeable Hours',align: 'left',value: 'chargeableHours', width:'5%',sortable:true},
                {text: 'Status',align: 'left',value: 'status', width:'5%',sortable:true},
            ],
            headers_all_mobile: [
                {text: 'Employee',align: 'left',value: 'lastName', width:'10%', sortable:true},
                {text: 'Date',align: 'left',value: 'transDate', width:'10%', sortable:true},
                {text: 'Status',align: 'left',value: 'status', width:'5%',sortable:true},
            ],
            overtime_summary: [],
            overtime_all: [],
            menu_status: false
        }
    },
    methods: {
        isCurrentPeriod(data){
            return data.cutOffPeriodDesc == 'Current'
        },
        setSelectedYear(fiscalYear) {
            this.selectedFiscalYear = fiscalYear
            updateCalendarYear(this.$route.name, fiscalYear)
            this.initialize()
        },
        viewCutoffDetails(data) {
            this.$router.push(`/company/overtime/${data.cutOffId}`)
        }, 
        viewDetails(data) {
            if (this.$store.state.userId == data.userId) {
                this.$router.push(`/myattendance/overtime/details/${data.overtimeId}`)
            }else{
                this.$router.push(`/company/overtime/details/${data.overtimeId}`)
            }
        }, 
        async getOvertimeSummary() {
            try {
                this.loading = true
                const response = await overtimeApi.getSummaryByCompany()     
                if (response && response.status == 200) {
                    this.overtime_summary = response.data.summaryItems
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        }, 
        async getAllTimeOff() {
            try {
                this.loading = true
                const response = await overtimeApi.getByFYByCompany(this.selectedFiscalYear)     
                if (response && response.status == 200) {
                    this.overtime_all = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        initialize() {
            if (this.viewAll) 
                this.getAllTimeOff()
            else
                this.getOvertimeSummary()
        }
    },
    created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'Company Overtime'
        })

        this.initialize()
    },
    watch: {
        'paginationSync1.rowsPerPage'(newVal) {
            updateRowsPerPage(`${this.$route.name}-table1`, newVal);
        },
        'paginationSync2.rowsPerPage'(newVal) {
            updateRowsPerPage(`${this.$route.name}-table2`, newVal);
        },
        'paginationSync1.sortBy'(newVal) {
            updateSort(`${this.$route.name}-table1`, newVal, this.paginationSync1.descending)
        },
        'paginationSync2.sortBy'(newVal) {
            updateSort(`${this.$route.name}-table2`, newVal, this.paginationSync2.descending)
        },
        'paginationSync1.descending'(newVal) {
            updateSort(`${this.$route.name}-table1`, this.paginationSync1.sortBy, newVal)
        },
        'paginationSync2.descending'(newVal) {
            updateSort(`${this.$route.name}-table2`, this.paginationSync2.sortBy, newVal)
        },
        viewAll(newVal) {
            updateViewAllSwitch(this.$route.name, newVal)
            this.initialize()
        },
        search(newVal) {
            updateSearchKeyword(this.$route.name, newVal)
        }
    },
    computed: {
        overtime_summary_filtered() {
            var temp = this.overtime_summary.filter(item => {
                return (item.fiscalYear==this.selectedFiscalYear && !item.cutOffPeriodDesc.includes('Upcoming'))
            })
            if (this.search) {
                temp = temp.filter(item => {
                    return ( item.cutOffStart.includes(this.search))
                })
            }
            return temp //.reverse()
        },
        overtime_all_filtered() {
            var temp = this.overtime_all

            //search handler
            if (this.search) {
                const searchLower = this.search.toLowerCase()
                temp = temp.filter(item => {
                    const transDateMatch = item.transDate.includes(this.search)
                    const teamNameMatch = item.teamName.toLowerCase().includes(searchLower)
                    const firstNameMatch = item.firstName.toLowerCase().includes(searchLower)
                    const lastNameMatch = item.lastName.toLowerCase().includes(searchLower)
                    return transDateMatch || teamNameMatch || firstNameMatch || lastNameMatch
                })
            }
            return temp 
        },
        fiscal_years() {
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods
            }
            return [{fiscalYear: new Date().getFullYear()}]
        },
        searchLabel() {
            if (this.viewAll) return 'Search team, date or name'
            return 'Search cutoff date'
        },
        helpPageId() {
            return 'overtime-review-company'
        },
    }
}
</script>